import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Download, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import OnboardingBanner from '../components/OnboardingBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerAcademic = () => {
  const { user } = useAuth();
  const isParent = user?.role === 'Parent';
  const [athlete, setAthlete] = useState(null);
  const [academicProfile, setAcademicProfile] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAcademicData(); }, []);

  const fetchAcademicData = async () => {
    try {
      const athletesRes = await axios.get(`${API}/athletes`);
      if (athletesRes.data.length > 0) {
        const athleteData = athletesRes.data[0];
        setAthlete(athleteData);
        const [profileRes, reportsRes] = await Promise.all([
          axios.get(`${API}/academic/athlete/${athleteData.id}`),
          axios.get(`${API}/academic-reports/athlete/${athleteData.id}`)
        ]);
        setAcademicProfile(profileRes.data);
        setReports(reportsRes.data);
      }
    } catch { toast.error('Failed to fetch academic data'); }
    finally { setLoading(false); }
  };

  const handleDownload = async (reportId, filename) => {
    try {
      const res = await axios.get(`${API}/academic-reports/${reportId}/attachment`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'school_report';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const getStatusBadge = (status) => ({
    excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    good_standing: 'bg-blue-100 text-blue-800 border-blue-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    probation: 'bg-red-100 text-red-800 border-red-200'
  })[status] || 'bg-blue-100 text-blue-800';

  const getStatusLabel = (s) => s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isParent ? "Your Child's Academic Progress" : 'My Academic Progress'}
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">
            {isParent ? "View your child's school performance and reports" : 'View your school performance and reports'}
          </p>
        </div>

        <OnboardingBanner
          id={isParent ? "parent-academic-v1" : "player-academic-v1"}
          description={isParent
            ? "Your child's school performance, academic status, and term reports. Academy staff upload reports here each period."
            : "Your school performance, academic status, and term reports. Reports are uploaded by academy staff each period."}
        />

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athlete ? (
          <Card data-testid="no-athlete-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">Profile not linked. Contact administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card data-testid="academic-profile-card">
              <CardHeader>
                <CardTitle>Academic Profile</CardTitle>
                <CardDescription>{isParent ? "Your child's current academic information" : 'Your current academic information'}</CardDescription>
              </CardHeader>
              <CardContent>
                {academicProfile ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">School</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100" data-testid="school-name">{academicProfile.school_name}</p>
                    </div>
                    {academicProfile.program_name && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Program</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{academicProfile.program_name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Academic Year</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{academicProfile.academic_year}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                      <Badge className={getStatusBadge(academicProfile.status)} data-testid="academic-status">
                        {getStatusLabel(academicProfile.status)}
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">No academic profile available yet</p>
                    <p className="text-xs text-gray-400 mt-1">Academic records will be added by academy staff.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Academic Reports</CardTitle>
                <CardDescription>{isParent ? "Your child's performance reports by period" : 'Your performance reports by period'}</CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No academic reports available yet</p>
                    <p className="text-xs text-gray-400 mt-1">Reports will appear here once uploaded by academy staff.</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="reports-list">
                    {reports.map((report) => (
                      <Card key={report.id} className="border-2" data-testid={`report-${report.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg">{report.period}</CardTitle>
                              {report.average_grade && (
                                <CardDescription className="text-base mt-1">
                                  Average Grade: <span className="font-semibold text-emerald-700">{report.average_grade}</span>
                                </CardDescription>
                              )}
                            </div>
                            {report.attachment_filename && (
                              <Button size="sm" variant="outline" onClick={() => handleDownload(report.id, report.attachment_filename)}
                                data-testid={`download-attachment-${report.id}`}>
                                <Download className="w-3 h-3 mr-1" /> School Report
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {report.subjects.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Subject Grades:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {report.subjects.map((subject) => (
                                  <div key={subject.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg" data-testid={`subject-${subject.id}`}>
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="font-medium text-gray-900 dark:text-gray-100">{subject.subject_name}</span>
                                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{subject.grade}</Badge>
                                    </div>
                                    {subject.comment && <p className="text-sm text-gray-500 mt-1">{subject.comment}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {report.teacher_comment && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                              <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Teacher's Comment</p>
                              <p className="text-sm text-blue-800 dark:text-blue-200" data-testid={`teacher-comment-${report.id}`}>{report.teacher_comment}</p>
                            </div>
                          )}
                          {report.academy_comment && (
                            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-300 mb-2">Academy Coordinator's Comment</p>
                              <p className="text-sm text-emerald-800 dark:text-emerald-200" data-testid={`academy-comment-${report.id}`}>{report.academy_comment}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayerAcademic;
